import type { CoverSheetConfigCard } from '../../types';

type BuildClientCardOptions = {
  id: string;
  entityType: 'client' | 'counterpartyClient';
  label: string;
  showSameAsProperty: boolean;
  requireNameAndEmail?: boolean;
};

export const buildClientCard = ({
  id,
  entityType,
  label,
  showSameAsProperty,
  requireNameAndEmail = false,
}: BuildClientCardOptions) => {
  const nameAndEmailRequired = entityType === 'client' || requireNameAndEmail;
  const nameAndEmailValidation = nameAndEmailRequired
    ? [
        {
          type: 'required',
          message: 'Required',
        },
      ]
    : [];

  const powerOfAttorneyEmailValidation =
    entityType === 'client'
      ? [
          {
            type: 'required',
            message: 'Required',
          },
        ]
      : [];

  return {
    id,
    entity: 'contacts',
    entityType,
    innerTitle: `${label} x`,
    outerTitle: `${label}s`,
    isAddable: true,
    addableTitle: `${label} x`,
    maxAddable: 4,
    addText: label,
    addByDefault: true,
    isCollapsible: true,
    syncsWithGoogleContacts: true,
    displayFields: [
      {
        name: `contacts.${entityType}.x.firstName`,
        type: 'name',
      },
      {
        name: `contacts.${entityType}.x.lastName`,
        type: 'name',
      },
      {
        name: `contacts.${entityType}.x.email`,
        type: 'email',
      },
      {
        name: `contacts.${entityType}.x.cellPhone`,
        type: 'phone',
      },
      {
        name: `contacts.${entityType}.x.street1`,
        type: 'address',
      },
      {
        name: `contacts.${entityType}.x.street2`,
        type: 'address',
      },
      {
        name: `contacts.${entityType}.x.city`,
        type: 'address',
      },
      {
        name: `contacts.${entityType}.x.state`,
        type: 'address',
      },
      {
        name: `contacts.${entityType}.x.zip`,
        type: 'address',
      },
    ],
    collapsedName: [
      `contacts.${entityType}.x.firstName`,
      `contacts.${entityType}.x.lastName`,
    ],
    layout: [
      ...(entityType === 'client'
        ? [
            [
              {
                name: `contacts.${entityType}.x.firstName`,
                size: 33,
                mobileSize: 50,
              },
              {
                name: `contacts.${entityType}.x.lastName`,
                size: 33,
                mobileSize: 50,
              },
              {
                name: `contacts.${entityType}.x.nickname`,
                size: 34,
                mobileSize: 100,
              },
            ],
          ]
        : [
            [
              {
                name: `contacts.${entityType}.x.firstName`,
                size: 50,
              },
              {
                name: `contacts.${entityType}.x.lastName`,
                size: 50,
              },
            ],
          ]),
      [
        {
          name: `contacts.${entityType}.x.email`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.cellPhone`,
          size: 50,
        },
        {
          name: `contacts.${entityType}.x.homePhone`,
          size: 50,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.street1`,
          size: 75,
        },
        {
          name: showSameAsProperty
            ? 'sameAsProperty'
            : `contacts.${entityType}.x.street2`,
          size: 25,
        },
      ],
      ...(showSameAsProperty
        ? [
            [
              {
                name: `contacts.${entityType}.x.street2`,
                size: 100,
              },
            ],
          ]
        : []),
      [
        {
          name: `contacts.${entityType}.x.city`,
          size: 50,
        },
        {
          name: `contacts.${entityType}.x.state`,
          size: 25,
          mobileSize: 50,
        },
        {
          name: `contacts.${entityType}.x.zip`,
          size: 25,
          mobileSize: 50,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.entityType`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.trust.name`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.isSuccessor`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.trust.title`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.trust.titleOther`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.probate.name`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.probate.title`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.probate.titleOther`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.entity.name`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.entity.title`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.entity.titleOther`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.powerOfAttorneyLabel`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.powerOfAttorney.firstName`,
          size: 50,
        },
        {
          name: `contacts.${entityType}.x.powerOfAttorney.lastName`,
          size: 50,
        },
      ],
      [
        {
          name: `contacts.${entityType}.x.powerOfAttorney.email`,
          size: 100,
        },
      ],
    ],
    fields: {
      firstName: {
        name: `contacts.${entityType}.x.firstName`,
        entity: 'contacts',
        entityType,
        type: 'autofill',
        label: 'First Name',
        placeholderText: '',
        action: {
          name: 'autofill',
          provider: 'googleContacts',
          label: {
            primary: 'fullName',
            secondary: 'email',
          },
          props: [
            {
              from: 'entityType',
              to: `contacts.${entityType}.x.entityType`,
            },
            {
              from: 'isSuccessor',
              to: `contacts.${entityType}.x.isSuccessor`,
            },
            {
              from: 'googleContactId',
              to: `contacts.${entityType}.x.googleContactId`,
            },
            {
              from: 'contactSource',
              to: `contacts.${entityType}.x.contactSource`,
            },
            {
              from: 'firstName',
              to: `contacts.${entityType}.x.firstName`,
            },
            {
              from: 'lastName',
              to: `contacts.${entityType}.x.lastName`,
            },
            {
              from: 'email',
              to: `contacts.${entityType}.x.email`,
            },
            {
              from: 'cellPhone',
              to: `contacts.${entityType}.x.cellPhone`,
            },
            {
              from: 'licenseNumber',
              to: `contacts.${entityType}.x.licenseNumber`,
            },
            {
              from: 'avatar',
              to: `contacts.${entityType}.x.avatar`,
            },
            {
              from: 'street1',
              to: `contacts.${entityType}.x.street1`,
            },
            {
              from: 'street2',
              to: `contacts.${entityType}.x.street2`,
            },
            {
              from: 'city',
              to: `contacts.${entityType}.x.city`,
            },
            {
              from: 'state',
              to: `contacts.${entityType}.x.state`,
            },
            {
              from: 'zip',
              to: `contacts.${entityType}.x.zip`,
            },
            {
              from: 'trust.name',
              to: `contacts.${entityType}.x.trust.name`,
            },
            {
              from: 'trust.title',
              to: `contacts.${entityType}.x.trust.title`,
            },
            {
              from: 'trust.titleOther',
              to: `contacts.${entityType}.x.trust.titleOther`,
            },
            {
              from: 'entity.name',
              to: `contacts.${entityType}.x.entity.name`,
            },
            {
              from: 'entity.title',
              to: `contacts.${entityType}.x.entity.title`,
            },
            {
              from: 'entity.titleOther',
              to: `contacts.${entityType}.x.entity.titleOther`,
            },
            {
              from: 'probate.name',
              to: `contacts.${entityType}.x.probate.name`,
            },
            {
              from: 'probate.title',
              to: `contacts.${entityType}.x.probate.title`,
            },
            {
              from: 'probate.titleOther',
              to: `contacts.${entityType}.x.probate.titleOther`,
            },
            {
              from: 'probate.name',
              to: `contacts.${entityType}.x.probate.name`,
            },
            {
              from: 'probate.title',
              to: `contacts.${entityType}.x.probate.title`,
            },
            {
              from: 'probate.titleOther',
              to: `contacts.${entityType}.x.probate.titleOther`,
            },
            {
              from: 'powerOfAttorney.firstName',
              to: `contacts.${entityType}.x.powerOfAttorney.firstName`,
            },
            {
              from: 'powerOfAttorney.lastName',
              to: `contacts.${entityType}.x.powerOfAttorney.lastName`,
            },
            {
              from: 'powerOfAttorney.email',
              to: `contacts.${entityType}.x.powerOfAttorney.email`,
            },
          ],
        },
        validation: [...nameAndEmailValidation],
      },
      lastName: {
        name: `contacts.${entityType}.x.lastName`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Last Name',
        placeholderText: '',
        validation: [...nameAndEmailValidation],
      },
      email: {
        name: `contacts.${entityType}.x.email`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Email',
        placeholderText: '',
        validation: [
          ...nameAndEmailValidation,
          {
            type: 'email',
            message: 'Invalid Email',
          },
        ],
      },
      cellPhone: {
        name: `contacts.${entityType}.x.cellPhone`,
        entity: 'contacts',
        entityType,
        type: 'phone',
        label: 'Cell Phone',
        placeholderText: '',
        validation: [
          {
            type: 'phone',
            message: 'Invalid phone number',
          },
        ],
      },
      homePhone: {
        name: `contacts.${entityType}.x.homePhone`,
        entity: 'contacts',
        entityType,
        type: 'phone',
        label: 'Home Phone',
        placeholderText: '',
        validation: [
          {
            type: 'phone',
            message: 'Invalid phone number',
          },
        ],
      },
      street1: {
        name: `contacts.${entityType}.x.street1`,
        entity: 'contacts',
        entityType,
        type: 'autofill',
        label: 'Street',
        placeholderText: '',
        action: {
          name: 'autofill',
          provider: 'googlePlacesSearch',
          label: {
            primary: 'label.primary',
            secondary: 'label.secondary',
          },
          props: [
            {
              from: 'street1',
              to: `contacts.${entityType}.x.street1`,
            },
            {
              from: 'street2',
              to: `contacts.${entityType}.x.street2`,
            },
            {
              from: 'city',
              to: `contacts.${entityType}.x.city`,
            },
            {
              from: 'state',
              to: `contacts.${entityType}.x.state`,
            },
            {
              from: 'county',
              to: `contacts.${entityType}.x.county`,
            },
            {
              from: 'zip',
              to: `contacts.${entityType}.x.zip`,
            },
          ],
        },
      },
      ...(showSameAsProperty
        ? {
            sameAsProperty: {
              name: 'sameAsProperty',
              type: 'actionButton',
              variant: 'outlined',
              label: 'Same as Property',
              action: {
                name: 'mapFromFields',
                props: [
                  {
                    from: 'property.street1',
                    to: `contacts.${entityType}.x.street1`,
                  },
                  {
                    from: 'property.street2',
                    to: `contacts.${entityType}.x.street2`,
                  },
                  {
                    from: 'property.city',
                    to: `contacts.${entityType}.x.city`,
                  },
                  {
                    from: 'property.state',
                    to: `contacts.${entityType}.x.state`,
                  },
                  {
                    from: 'property.zip',
                    to: `contacts.${entityType}.x.zip`,
                  },
                ],
              },
            },
          }
        : {}),
      street2: {
        name: `contacts.${entityType}.x.street2`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Apt/Unit/Suite Number',
        placeholderText: '',
      },
      city: {
        name: `contacts.${entityType}.x.city`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'City',
        placeholderText: '',
      },
      state: {
        name: `contacts.${entityType}.x.state`,
        entity: 'contacts',
        entityType,
        type: 'state',
        label: 'State',
        placeholderText: '',
      },
      zip: {
        name: `contacts.${entityType}.x.zip`,
        entity: 'contacts',
        entityType,
        type: 'zip',
        label: 'Zip Code',
        placeholderText: '',
      },
      ...(entityType === 'client'
        ? {
            nickname: {
              name: `contacts.${entityType}.x.nickname`,
              entity: 'contacts',
              entityType,
              type: 'text',
              label: 'Nickname',
            },
          }
        : {}),
      entityType: {
        name: `contacts.${entityType}.x.entityType`,
        entity: 'contacts',
        entityType,
        type: 'radio',
        label: 'Ownership:',
        value: 'individual',
        options: [
          {
            label: 'Individual',
            value: 'individual',
          },
          {
            label: 'Trust',
            value: 'trust',
          },
          {
            label: 'Entity (LLC, Corp, Other)',
            value: 'entity',
          },
          {
            label: 'Probate/Court Administration',
            value: 'probate',
          },
          {
            label: 'Power of Attorney',
            value: 'powerOfAttorney',
          },
        ],
        placeholderText: '',
      },
      trustName: {
        name: `contacts.${entityType}.x.trust.name`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Trust Name',
        placeholderText: 'the John Smith Family Trust, dated 19XX',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'trust',
        },
      },
      isSuccessor: {
        name: `contacts.${entityType}.x.isSuccessor`,
        type: 'checkbox',
        label: 'Trustee is Successor',
        entity: 'contacts',
        entityType,
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'trust',
        },
        tooltip:
          '"Successor Trustees" is/are the currently acting Trustee (or Co-Trustees) being appointed following the death of the original trustees',
      },
      trustTitle: {
        name: `contacts.${entityType}.x.trust.title`,
        entity: 'contacts',
        entityType,
        type: 'radio',
        label: 'Client Title:',
        value: '',
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'trust',
        },
        options: [
          {
            label: 'Successor Trustee',
            value: 'successorTrustee',
            showWhen: {
              fieldName: `contacts.${entityType}.x.isSuccessor`,
              hasValue: 'true',
            },
          },
          {
            label: 'Trustee',
            value: 'trustee',
            showWhen: {
              fieldName: `contacts.${entityType}.x.isSuccessor`,
              hasValue: 'false',
            },
          },
          {
            label: 'Other',
            value: 'other',
          },
        ],
        placeholderText: '',
      },
      trustTitleOther: {
        name: `contacts.${entityType}.x.trust.titleOther`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: '',
        placeholderText: 'Please Specify',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.trust.title`,
          hasValue: 'other',
        },
      },
      probateName: {
        name: `contacts.${entityType}.x.probate.name`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Estate Name',
        placeholderText: 'Estate of John Smith',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'probate',
        },
      },
      probateTitle: {
        name: `contacts.${entityType}.x.probate.title`,
        entity: 'contacts',
        entityType,
        type: 'radio',
        label: 'Client Title:',
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'probate',
        },
        options: [
          {
            label: 'Administrator',
            value: 'administrator',
          },
          {
            label: 'Executor',
            value: 'executor',
          },
          {
            label: 'Conservator',
            value: 'conservator',
          },
          {
            label: 'Other',
            value: 'other',
          },
        ],
        placeholderText: '',
      },
      probateTitleOther: {
        name: `contacts.${entityType}.x.probate.titleOther`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: '',
        placeholderText: 'Please Specify',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.probate.title`,
          hasValue: 'other',
        },
      },
      entityName: {
        name: `contacts.${entityType}.x.entity.name`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Entity Name',
        placeholderText: 'Fox Holdings, Inc',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'entity',
        },
      },
      entityTitle: {
        name: `contacts.${entityType}.x.entity.title`,
        entity: 'contacts',
        entityType,
        type: 'radio',
        label: 'Client Title:',
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'entity',
        },
        options: [
          {
            label: 'CEO',
            value: 'ceo',
          },
          {
            label: 'Partner',
            value: 'partner',
          },
          {
            label: 'Managing Partner',
            value: 'managingPartner',
          },
          {
            label: 'Other',
            value: 'other',
          },
        ],
        placeholderText: '',
      },
      entityTitleOther: {
        name: `contacts.${entityType}.x.entity.titleOther`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: '',
        placeholderText: 'Please Specify',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entity.title`,
          hasValue: 'other',
        },
      },
      powerOfAttorneyLabel: {
        name: `contacts.${entityType}.x.powerOfAttorneyLabel`,
        type: 'label',
        label: 'Authorized Signer / Attorney in Fact:',
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'powerOfAttorney',
        },
      },
      powerOfAttorneyFirstName: {
        name: `contacts.${entityType}.x.powerOfAttorney.firstName`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'First Name',
        placeholderText: '',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'powerOfAttorney',
        },
      },
      powerOfAttorneyLastName: {
        name: `contacts.${entityType}.x.powerOfAttorney.lastName`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Last Name',
        placeholderText: '',
        validation: [
          {
            type: 'required',
            message: 'Required',
          },
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'powerOfAttorney',
        },
      },
      powerOfAttorneyEmail: {
        name: `contacts.${entityType}.x.powerOfAttorney.email`,
        entity: 'contacts',
        entityType,
        type: 'text',
        label: 'Email',
        placeholderText: '',
        validation: [
          {
            type: 'email',
            message: 'Invalid email',
          },
          ...powerOfAttorneyEmailValidation,
        ],
        showWhen: {
          fieldName: `contacts.${entityType}.x.entityType`,
          hasValue: 'powerOfAttorney',
        },
      },
    },
  } satisfies CoverSheetConfigCard;
};
